import { useState, useEffect, useCallback } from 'react';
import { GameState, Move, GameEngineUtils } from '../services/gameEngineApi';

// Legacy frontend types for compatibility with existing board component
export interface LegacyPieceState {
  id: string;
  position: number;
  hasCompletedCircuit: boolean;
  isInHomeStretch: boolean;
  lastMoveId: string;
}

export interface LegacyPlayer {
  id: string;
  name: string;
  color: 'red' | 'blue' | 'green' | 'yellow';
  pieces: LegacyPieceState[];
}

export interface LegacyGameState {
  players: LegacyPlayer[];
  currentPlayer: number;
  diceValue: number | null;
  gameStatus: 'waiting' | 'playing' | 'finished';
  winner: string | null;
  canRollAgain: boolean;
  selectedPiece: number | null;
  validMoves: number[];
}

interface UseLegacyGameStateProps {
  backendGameState: GameState | null;
  availableMoves: Move[];
}

export const useLegacyGameState = ({
  backendGameState,
  availableMoves,
}: UseLegacyGameStateProps) => {
  const [legacyGameState, setLegacyGameState] = useState<LegacyGameState>({
    players: [],
    currentPlayer: 0,
    diceValue: null,
    gameStatus: 'waiting',
    winner: null,
    canRollAgain: false,
    selectedPiece: null,
    validMoves: [],
  });

  // Convert backend game state to legacy format for existing board component
  const convertToLegacyGameState = useCallback(
    (backendState: GameState): LegacyGameState => {
      const legacyPlayers: LegacyPlayer[] = backendState.players.map(
        (player, index) => ({
          id: (index + 1).toString(),
          name: player.displayName,
          color: GameEngineUtils.mapPlayerColor(player.color),
          pieces: player.pieces.map((piece, pieceIndex) => ({
            id: piece.id,
            position: piece.position,
            hasCompletedCircuit: piece.position > 50, // Simplified logic
            isInHomeStretch: piece.position >= 100, // Simplified logic
            lastMoveId: `${piece.id}-${Date.now()}`,
          })),
        })
      );

      // Calculate valid moves (piece indices that can move)
      const currentPlayerId =
        backendState.players[backendState.currentPlayerIndex]?.id;
      const validMoveIndices = availableMoves
        .filter((move) => move.playerId === currentPlayerId)
        .map((move) => {
          // Find piece index for this move
          const currentPlayer =
            backendState.players[backendState.currentPlayerIndex];
          return currentPlayer.pieces.findIndex(
            (piece) => piece.id === move.pieceId
          );
        })
        .filter((index) => index !== -1);

      // Safely convert backend game state with fallback
      let gameStatus: 'waiting' | 'playing' | 'finished' = 'waiting';
      try {
        const convertedState =
          GameEngineUtils.convertBackendGameState(backendState);
        if (convertedState && convertedState.gameStatus) {
          gameStatus = convertedState.gameStatus as
            | 'waiting'
            | 'playing'
            | 'finished';
        } else {
          // Fallback to direct mapping if conversion fails
          gameStatus =
            backendState.gamePhase === 'waiting'
              ? 'waiting'
              : backendState.gamePhase === 'active'
                ? 'playing'
                : 'finished';
        }
      } catch (error) {
        console.warn(
          'GameEngineUtils.convertBackendGameState failed, using fallback:',
          error
        );
        // Fallback to direct mapping
        gameStatus =
          backendState.gamePhase === 'waiting'
            ? 'waiting'
            : backendState.gamePhase === 'active'
              ? 'playing'
              : 'finished';
      }

      return {
        players: legacyPlayers,
        currentPlayer: backendState.currentPlayerIndex,
        diceValue: backendState.lastDiceRoll,
        gameStatus,
        winner: backendState.winner,
        canRollAgain: false, // Will be determined by backend response
        selectedPiece: null,
        validMoves: validMoveIndices,
      };
    },
    [availableMoves]
  );

  // Update legacy state when backend state changes
  useEffect(() => {
    if (backendGameState) {
      const converted = convertToLegacyGameState(backendGameState);
      setLegacyGameState(converted);
    }
  }, [backendGameState, convertToLegacyGameState]);

  return {
    legacyGameState,
    setLegacyGameState,
  };
};
